/**
 * CdaGateLadder — the governed-execution path for a tool, made legible.
 *
 * TRUST CONTRACT (the one decision this whole kit hangs on):
 *   The ladder carries an explicit `provenance`:
 *     - 'predicted' (author / simulate): a hypothetical preview labeled as such.
 *       planGates() is a read-only mirror of the Gate Planner; a confident-but-
 *       wrong preview is acceptable because the frame is openly hypothetical.
 *     - 'enforced' (operator): anchored on the receipts/PDP decision as the
 *       source of truth. The contract-derived ladder is labeled "declared" and
 *       RECONCILED against recent executions — "contract declares X; last N
 *       executions enforced Y" — with divergence flagged. When no executions
 *       exist it falls back to "declared — not yet observed" and NEVER fabricates
 *       an enforced claim.
 *
 * VISUAL: two channels (risk vs coverage), decision-gates-first (mechanical
 * preparation steps collapse), Lucide-style icons, opt-in motion (on for
 * authoring/simulate, off for the high-frequency operator console).
 */

import type { CdaContract } from './types';
import { planGates } from './planGates';
import type { GateStep, GateState } from './planGates';
import { gateChannelColor, COVERAGE_PRESENT, COVERAGE_STRONG, NEUTRAL, GAP_COLOR, softFill, softBorder, TEXT_PRIMARY, TEXT_SECONDARY } from './colors';
import { CdaIcon } from './icons';
import type { CdaIconName } from './icons';

const React = (globalThis as any).React;
const { useState, useMemo, useCallback } = React;

/** Observed enforcement reconstructed from receipts/PDP (operator surface). */
export interface EnforcedObservation {
  /** Number of recent executions the reconciliation is drawn from. */
  executionCount: number;
  /** gate id -> was this declared gate actually observed in recent executions? */
  observed: Record<string, boolean>;
  /** Gates enforced at runtime that the contract did NOT declare (divergence). */
  extraEnforced?: Array<{ id: string; label: string; detail?: string }>;
  /** ISO timestamp of the most recent observed execution. */
  lastExecutedAt?: string;
}

export interface CdaGateLadderProps {
  contract: CdaContract;
  provenance: 'predicted' | 'enforced';
  /** Simulated arguments — predicted mode only. */
  args?: Record<string, unknown>;
  /** Enable staggered entrance motion (authoring/simulate only). */
  motion?: boolean;
  /** Reconciliation data — enforced mode only. */
  enforced?: EnforcedObservation | null;
  onLearnMore?: () => void;
  className?: string;
}

const COLLAPSIBLE_IDS: ReadonlySet<string> = new Set(['defaults', 'normalize']);

const ICON_BY_ID: Record<string, CdaIconName> = {
  defaults: 'wand',
  normalize: 'wand',
  inputs: 'circle-dot',
  'secure-input': 'eye-off',
  confirm: 'shield-check',
  waiting: 'clock',
  'step-up': 'key',
  consent: 'lock',
  disclosure: 'eye',
  authorize: 'shield-check',
  receipt: 'file-check',
};

function iconForStep(step: GateStep): CdaIconName {
  if (ICON_BY_ID[step.id]) return ICON_BY_ID[step.id];
  switch (step.state) {
    case 'satisfied':
      return 'check';
    case 'waiting':
      return 'clock';
    case 'blocked':
      return 'alert-triangle';
    case 'skipped':
      return 'minus';
    default:
      return 'circle-dot';
  }
}

function stepColor(step: GateStep): string {
  if (step.state === 'satisfied') return COVERAGE_STRONG;
  if (step.state === 'blocked') return 'var(--color-danger, #ef4444)';
  if (step.state === 'skipped') return NEUTRAL;
  return gateChannelColor(step.channel);
}

interface ReconStatus {
  text: string;
  tone: 'declared' | 'enforced' | 'divergent' | 'predicted';
}

function reconStatusFor(
  step: GateStep,
  provenance: 'predicted' | 'enforced',
  enforced?: EnforcedObservation | null,
): ReconStatus | null {
  if (provenance === 'predicted') {
    return step.kind === 'decision' ? { text: 'predicted', tone: 'predicted' } : null;
  }
  // enforced
  if (!enforced || enforced.executionCount === 0) {
    return step.kind === 'decision' ? { text: 'declared — not yet observed', tone: 'declared' } : null;
  }
  if (step.kind !== 'decision' && step.id !== 'receipt' && step.id !== 'authorize') return null;
  const observed = enforced.observed[step.id];
  if (observed === true) {
    return { text: `enforced · ${enforced.executionCount} exec`, tone: 'enforced' };
  }
  // Explicit false = the contract declares it but recent executions did NOT
  // enforce it → genuine divergence. Unknown (undefined) = we could not observe
  // this gate from the available receipts; stay neutral rather than fabricate
  // an alarm (the trust contract: never overstate enforcement OR divergence).
  if (observed === false) {
    return { text: 'declared, not enforced', tone: 'divergent' };
  }
  return { text: 'declared', tone: 'declared' };
}

const RECON_TONE_COLOR: Record<ReconStatus['tone'], string> = {
  declared: NEUTRAL,
  predicted: COVERAGE_PRESENT,
  enforced: COVERAGE_STRONG,
  divergent: GAP_COLOR,
};

export function CdaGateLadder({
  contract,
  provenance,
  args,
  motion = false,
  enforced,
  onLearnMore,
  className,
}: CdaGateLadderProps) {
  const [showPrep, setShowPrep] = useState(false);
  const [mounted, setMounted] = useState(!motion);

  React.useEffect(() => {
    if (!motion) return;
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setMounted(true);
      return;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [motion]);

  const allSteps: GateStep[] = useMemo(() => {
    const contractSteps = planGates(contract, args ? { args } : undefined);
    // Platform invariants — framing rows the contract does not declare.
    const authorize: GateStep = {
      id: 'authorize',
      label: 'Authorized live by PDP',
      kind: 'decision',
      state:
        provenance === 'enforced' && enforced && enforced.executionCount > 0
          ? enforced.observed.authorize
            ? 'satisfied'
            : 'required'
          : 'required',
      detail: contract['x-operation-ref']
        ? `Operation ${contract['x-operation-ref']} — evaluated on every call`
        : 'AuthZEN decision evaluated on every call',
      channel: 'coverage',
    };
    const receipt: GateStep = {
      id: 'receipt',
      label: 'Signed receipt recorded',
      kind: 'mechanical',
      state: provenance === 'enforced' && enforced && enforced.executionCount > 0 ? 'satisfied' : 'auto',
      detail: 'Seven-band signed receipt captured for audit',
      channel: 'coverage',
    };
    return [...contractSteps, authorize, receipt];
  }, [contract, args, provenance, enforced]);

  const prepSteps = allSteps.filter((s) => COLLAPSIBLE_IDS.has(s.id));
  const mainSteps = allSteps.filter((s) => !COLLAPSIBLE_IDS.has(s.id));

  const divergence =
    provenance === 'enforced' &&
    enforced &&
    enforced.executionCount > 0 &&
    (mainSteps.some(
      (s) => s.kind === 'decision' && enforced.observed[s.id] === false,
    ) ||
      (enforced.extraEnforced && enforced.extraEnforced.length > 0));

  const renderStep = useCallback(
    (step: GateStep, index: number, muted: boolean) => {
      const color = muted ? NEUTRAL : stepColor(step);
      const recon = reconStatusFor(step, provenance, enforced);
      return React.createElement(
        'li',
        {
          key: step.id,
          'data-testid': `cda-gate-${step.id}`,
          'data-gate-state': step.state,
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '7px 0',
            opacity: mounted ? (muted ? 0.75 : 1) : 0,
            transform: mounted ? 'none' : 'translateY(4px)',
            transition: motion ? `opacity 220ms ease-out ${index * 45}ms, transform 220ms ease-out ${index * 45}ms` : 'none',
          },
        },
        React.createElement(
          'span',
          {
            'aria-hidden': 'true',
            style: {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              flexShrink: 0,
              color,
              background: softFill(color, 12),
              border: `1px solid ${softBorder(color, 30)}`,
            },
          },
          React.createElement(CdaIcon, { name: iconForStep(step), size: 14, color }),
        ),
        React.createElement(
          'span',
          { style: { flex: 1, minWidth: 0 } },
          React.createElement(
            'span',
            { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
            React.createElement(
              'span',
              { style: { fontSize: 13, fontWeight: step.kind === 'decision' ? 600 : 500, color: TEXT_PRIMARY } },
              step.label,
            ),
            recon &&
              React.createElement(
                'span',
                {
                  'data-testid': `cda-gate-recon-${step.id}`,
                  style: {
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: 'var(--border-radius-sm, 6px)',
                    color: RECON_TONE_COLOR[recon.tone],
                    background: softFill(RECON_TONE_COLOR[recon.tone], 12),
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  },
                },
                recon.text,
              ),
          ),
          step.detail &&
            React.createElement(
              'span',
              { style: { display: 'block', fontSize: 12, color: TEXT_SECONDARY, marginTop: 2, lineHeight: 1.45 } },
              step.detail,
            ),
        ),
      );
    },
    [mounted, motion, provenance, enforced],
  );

  const hasObservedExecutions = provenance === 'enforced' && !!(enforced && enforced.executionCount > 0);
  const provenanceLabel =
    provenance === 'predicted'
      ? args
        ? 'Simulated'
        : 'Predicted'
      : hasObservedExecutions
        ? 'Enforced'
        : 'Declared — not yet observed';
  const provenanceColor = provenance === 'predicted' ? COVERAGE_PRESENT : hasObservedExecutions ? COVERAGE_STRONG : NEUTRAL;
  const provenanceHint =
    provenance === 'predicted'
      ? args
        ? 'Hypothetical: what these arguments would cause under the contract.'
        : 'Hypothetical: what this contract would cause. Not a runtime record.'
      : enforced && enforced.executionCount > 0
        ? `Reconciled against the last ${enforced.executionCount} execution${enforced.executionCount === 1 ? '' : 's'} (signed receipts).`
        : 'No executions observed yet — showing the declared contract path.';

  return React.createElement(
    'div',
    {
      'data-testid': 'cda-gate-ladder',
      'data-provenance': provenance,
      className: className || undefined,
    },
    // Header: provenance pill + hint + optional learn-more
    React.createElement(
      'div',
      { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' } },
      React.createElement(
        'span',
        {
          'data-testid': 'cda-ladder-provenance',
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            padding: '2px 8px',
            borderRadius: 'var(--border-radius-sm, 999px)',
            color: provenanceColor,
            background: softFill(provenanceColor, 14),
            border: `1px solid ${softBorder(provenanceColor, 32)}`,
          },
        },
        React.createElement(CdaIcon, { name: hasObservedExecutions ? 'file-check' : provenance === 'predicted' ? 'wand' : 'circle-dot', size: 12, color: provenanceColor }),
        provenanceLabel,
      ),
      React.createElement(
        'span',
        { style: { fontSize: 11, color: TEXT_SECONDARY } },
        provenanceHint,
      ),
      onLearnMore &&
        React.createElement(
          'button',
          {
            'data-testid': 'cda-ladder-learn-more',
            onClick: onLearnMore,
            style: {
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 11,
              color: COVERAGE_PRESENT,
              fontFamily: 'inherit',
              padding: 0,
            },
          },
          'Learn more',
          React.createElement(CdaIcon, { name: 'external-link', size: 11, color: COVERAGE_PRESENT }),
        ),
    ),
    // Divergence banner (enforced only)
    divergence &&
      React.createElement(
        'div',
        {
          'data-testid': 'cda-ladder-divergence',
          role: 'alert',
          style: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '8px 10px',
            borderRadius: 'var(--border-radius-sm, 8px)',
            background: softFill(GAP_COLOR, 14),
            border: `1px solid ${softBorder(GAP_COLOR, 40)}`,
            color: GAP_COLOR,
            fontSize: 12,
            marginBottom: 8,
          },
        },
        React.createElement(CdaIcon, { name: 'alert-triangle', size: 14, color: GAP_COLOR }),
        React.createElement(
          'span',
          null,
          'Declared governance diverges from what recent executions actually enforced. Review the receipts below.',
        ),
      ),
    // Collapsible preparation (mechanical) steps
    prepSteps.length > 0 &&
      React.createElement(
        'div',
        { style: { marginBottom: 2 } },
        React.createElement(
          'button',
          {
            'data-testid': 'cda-ladder-prep-toggle',
            onClick: () => setShowPrep((v: boolean) => !v),
            'aria-expanded': showPrep,
            style: {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 11,
              color: TEXT_SECONDARY,
              fontFamily: 'inherit',
              padding: '4px 0',
            },
          },
          React.createElement(CdaIcon, { name: showPrep ? 'chevron-down' : 'chevron-right', size: 13, color: TEXT_SECONDARY }),
          `${prepSteps.length} automatic preparation step${prepSteps.length === 1 ? '' : 's'} (${prepSteps.map((s) => s.label.toLowerCase()).join(', ')})`,
        ),
        showPrep &&
          React.createElement(
            'ul',
            { style: { listStyle: 'none', margin: 0, padding: '0 0 0 4px' } },
            ...prepSteps.map((step, i) => renderStep(step, i, true)),
          ),
      ),
    // Main (decision + always-shown coverage) steps
    React.createElement(
      'ul',
      { 'data-testid': 'cda-ladder-steps', style: { listStyle: 'none', margin: 0, padding: 0 } },
      ...mainSteps.map((step, i) => renderStep(step, i, false)),
      // Divergent extra-enforced rows (gates enforced but not declared)
      ...(provenance === 'enforced' && enforced && enforced.extraEnforced
        ? enforced.extraEnforced.map((extra, i) =>
            React.createElement(
              'li',
              {
                key: `extra-${extra.id}`,
                'data-testid': `cda-gate-extra-${extra.id}`,
                style: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0' },
              },
              React.createElement(
                'span',
                {
                  'aria-hidden': 'true',
                  style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    flexShrink: 0,
                    color: GAP_COLOR,
                    background: softFill(GAP_COLOR, 12),
                    border: `1px solid ${softBorder(GAP_COLOR, 30)}`,
                  },
                },
                React.createElement(CdaIcon, { name: 'alert-triangle', size: 14, color: GAP_COLOR }),
              ),
              React.createElement(
                'span',
                { style: { flex: 1 } },
                React.createElement(
                  'span',
                  { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                  React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY } }, extra.label),
                  React.createElement(
                    'span',
                    {
                      style: {
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '1px 6px',
                        borderRadius: 6,
                        color: GAP_COLOR,
                        background: softFill(GAP_COLOR, 12),
                        textTransform: 'uppercase',
                      },
                    },
                    'enforced, not declared',
                  ),
                ),
                extra.detail &&
                  React.createElement('span', { style: { display: 'block', fontSize: 12, color: TEXT_SECONDARY, marginTop: 2 } }, extra.detail),
              ),
            ),
          )
        : []),
    ),
  );
}
